import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box,
} from '@mui/material';
import { MAX_BRAND_LENGTH } from '../utils/appBrand';
import { barModeButtonLabel } from '../utils/toolbarBarMode';

/**
 * Renaming the application, for one browser.
 *
 * <p>Presentational: it reports what was chosen and the caller decides what that means. Keeping the
 * storage out of here is what lets the same dialog be driven from a test without a localStorage
 * stub, and what stops "the dialog closed" and "the name changed" becoming the same event.
 */
export default function AppBrandDialog({
  open, currentName, defaultName, onSave, onReset, onClose, barMode, onToggleBarMode,
}) {
  const [value, setValue] = useState(currentName || '');

  // Re-seeded on each OPEN, not on every render. Seeding on render would fight the user's typing;
  // seeding once at mount would show a stale name the second time the dialog was opened.
  useEffect(() => {
    if (open) setValue(currentName || '');
  }, [open, currentName]);

  const trimmed = value.trim();
  const tooLong = value.length > MAX_BRAND_LENGTH;
  // Blank is refused rather than accepted-as-reset: the two buttons already say which is which, and
  // a Save that silently did a Reset would be the surprising one.
  const canSave = trimmed.length > 0 && !tooLong;

  const save = () => { if (canSave) onSave(trimmed); };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Update Application Name</DialogTitle>

      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label="Application name"
          value={value}
          onChange={e => setValue(e.target.value)}
          // Enter saves. A single-field dialog where the obvious key does nothing reads as broken.
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
          error={tooLong}
          helperText={tooLong
            ? `${value.length} characters — the header fits about ${MAX_BRAND_LENGTH}.`
            : `Shown as "${trimmed || defaultName} - SYMBOL" in the header and the browser tab.`}
          inputProps={{ 'aria-label': 'Application name', maxLength: MAX_BRAND_LENGTH + 20 }}
        />

        {/*
          The toolbar preset lives here rather than on the toolbar itself, because switching it also
          renames the application - and a control that renames the product belongs beside the box
          that renames the product, not among fifty chart tools.
        */}
        {typeof onToggleBarMode === 'function' && (
          <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button variant="outlined" size="small" onClick={onToggleBarMode}>
              {barModeButtonLabel(barMode)}
            </Button>
            <Typography sx={{ fontSize: 12, color: '#666' }}>
              {/* Says what the button will DO, since its label is the destination rather than the
                  current state. */}
              Switches the toolbar preset and the application name.
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: 12, color: '#666' }}>
            {/* Said plainly, because "why is my colleague's header different" is the question this
                setting generates. It is a per-browser preference, not an account setting. */}
            This name is stored in this browser only. It does not change what other people see, and
            it does not appear on the chart watermark.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        {/* Reset sits apart from the pair on the right: it is the destructive one, and putting it
            beside Cancel is how it gets pressed by someone aiming for Cancel. */}
        <Button onClick={onReset} color="warning">Reset to Default</Button>
        <Box>
          <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
          <Button onClick={save} variant="contained" disabled={!canSave}>Save</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

AppBrandDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  /** What the header shows right now — the override, or the market's own brand. */
  currentName: PropTypes.string,
  /** What Reset would fall back to, used in the preview text. */
  defaultName: PropTypes.string,
  /** Called with the trimmed, non-empty name. */
  onSave: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  /** 'short' or 'long' — decides the toggle's label. */
  barMode: PropTypes.string,
  /** Flips the preset. Omitted, the toggle is not rendered at all. */
  onToggleBarMode: PropTypes.func,
};
